import {
  ExItemRef,
  ExItemSelect,
  type Props as ExItemSelectProps,
} from '@/components/ExItemSelect';
import { PaymentRequestForm } from '@/features/InvoiceReport/Edit/type';
import { memo, useCallback, useMemo, useRef } from 'react';
import { FieldPath, useFormContext, useWatch } from 'react-hook-form';
import { CellProps } from './type';

export type Props<TFieldValues extends PaymentRequestForm> =
  ExItemSelectProps<TFieldValues> & {
    drExciseIdName: FieldPath<PaymentRequestForm>;
  } & CellProps;

const InnerExItemSelect = <TFieldValues extends PaymentRequestForm>({
  drExciseIdName,
  onChangeAfter,
  index,
  deptIdName,
  ...rest
}: Props<TFieldValues>) => {
  const { control, deptId: deptIdProp } = rest;
  // Use watched value if deptIdName exists, otherwise fall back to prop
  const watchedDeptId = deptIdName
    ? // eslint-disable-next-line react-hooks/rules-of-hooks, @typescript-eslint/consistent-type-assertions
      (useWatch<TFieldValues>({
        name: deptIdName,
        control,
      }) as string | undefined)
    : undefined;
  const deptId = watchedDeptId ?? deptIdProp;
  const cacheKey = useMemo(
    () => ['row', 'ex_item', index, deptId ?? ''],
    [index, deptId]
  );
  const inputRef = useRef<ExItemRef>(null);
  const { setValue } = useFormContext();
  const setLabelField = useCallback(
    (labelFieldValue: string) => {
      setValue(`invoiceTransactions.${index}.exItemName`, labelFieldValue);
    },
    [setValue, index]
  );

  const onChanged = useCallback(
    (value: string | number | (string | number)[]) => {
      // 経費科目に紐付く税区分をセットする
      const item = inputRef.current?.selectedItem();
      setValue(drExciseIdName, item?.dr_excise_id);

      onChangeAfter?.(value);
    },
    [drExciseIdName, onChangeAfter, setValue]
  );
  return (
    <ExItemSelect
      per={500}
      ref={inputRef}
      {...rest}
      onChangeAfter={onChanged}
      setLabelField={setLabelField}
      cacheQueryKey={cacheKey}
    />
  );
};
InnerExItemSelect.displayName = 'InnerExItemSelect';

// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
export const ExItemSelectCell = memo(
  InnerExItemSelect
) as typeof InnerExItemSelect;
